import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import TopBar from '../../../componentsv2/TopBar';
import MarcoLogo from '../../../componentsv2/MarcoLogo';
import Typography from '../../../componentsv2/Typography';
import { TextField } from '../../../componentsv2/Field';
import Button from '../../../componentsv2/Button';
import axios from '../../../services/axios';
import { useLazyTranslation } from '../../../hooks/i18n';
import { LOGIN } from '../../../constants/auth';
import { COLORS } from '../../../themev2';

const ResetPasswordSchema = (t: any) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common:signup.form.email.errors.invalid'))
      .required(t('common:requiredField')),
  });

const ResetPassword = () => {
  const { t } = useLazyTranslation();
  const navigate = useNavigate();
  const [sentRecoveryEmailToUser, setSentRecoveryEmailToUser] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (
    values: { email: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setErrorMessage('');
    logEvent('Password Reset - Clicked', { email: values.email });

    try {
      await axios.post(`/password/password-reset/`, { email: values.email });
      setSentRecoveryEmailToUser(true);
      logEvent('Password Reset - Success', { email: values.email });
    } catch (error: any) {
      setErrorMessage(error.response?.data?.email);
      logEvent('Password Reset - Failed', { email: values.email });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <TopBar>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton href="/">
            <MarcoLogo />
          </IconButton>
        </Box>
      </TopBar>
      <Box component="main" sx={{ flexGrow: 1, p: 8, backgroundColor: COLORS.gray[300] }}>
        <Container
          sx={{
            maxWidth: { xs: '100%', sm: 400, md: 500 },
            padding: '1em 0em',
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h4" textAlign={{ xs: 'center', md: 'left' }} fontWeight={700}>
                {sentRecoveryEmailToUser ? t('reset.titleToConfirm') : t('reset.titleToReset')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                textAlign={{ xs: 'center', md: 'left' }}
                sx={{ fontSize: '18px', lineHeight: '27px', color: '#66758F' }}
              >
                {sentRecoveryEmailToUser ? t('reset.subtitleToConfirm') : t('reset.subtitleToReset')}
              </Typography>
            </Grid>
            {!sentRecoveryEmailToUser && (
              <Grid item xs={12}>
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={ResetPasswordSchema(t)}
                  onSubmit={handleSubmit}
                  validateOnChange={false}
                  validateOnBlur={true}
                >
                  {({ values, handleSubmit, isSubmitting }) => (
                    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            name="email"
                            label={t('reset.emailLabel')}
                            placeholder={t('reset.emailPlaceholder')}
                          />
                          {errorMessage && (
                            <Typography color="error" sx={{ mt: 1 }}>
                              {errorMessage}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sx={{ marginTop: '5em' }}>
                          <Button
                            fullWidth
                            color="secondary"
                            isLoading={isSubmitting}
                            disabled={!values.email || isSubmitting}
                            isSubmit
                          >
                            {t('reset.recoverPassword')}
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="outline"
                            color="tertiary"
                            fullWidth
                            onClick={() => {
                              logEvent('Password Reset - Canceled');
                              navigate(LOGIN);
                            }}
                          >
                            {t('reset.cancel')}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </Grid>
            )}
            {sentRecoveryEmailToUser && (
              <Grid item xs={12} sx={{ marginTop: '5em' }}>
                <Button
                  color="secondary"
                  fullWidth
                  onClick={() => {
                    logEvent('Password Reset - Navigate to Login');
                    navigate(LOGIN);
                  }}
                >
                  {t('reset.goToLogin')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ResetPassword;
